import React, { useState } from 'react';
import { MessageSquareIcon, CloudIcon } from './Icons.jsx';
import './MentimeterPollDisplay.css';

export default function MentimeterPollDisplay({ tally }) {
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'cloud'
  const words = tally?.words || [];
  const responses = tally?.responses || [];

  // Deduplicate responses or group by text
  const groupedCards = React.useMemo(() => {
    if (words.length > 0) {
      return [...words].sort((a, b) => b.count - a.count);
    }
    const map = new Map();
    for (const r of responses) {
      const text = r.text?.trim();
      if (text) {
        map.set(text, (map.get(text) || 0) + 1);
      }
    }
    return [...map.entries()].map(([text, count]) => ({ text, count }));
  }, [words, responses]);

  return (
    <div className="menti-container stack">
      {/* Top switch bar */}
      {groupedCards.length > 0 && (
        <div className="menti-top-bar row-between">
          <span className="menti-total-count">
            ความคิดเห็น ({groupedCards.reduce((acc, c) => acc + c.count, 0)})
          </span>
          <div className="menti-view-toggle row">
            <button
              type="button"
              className={`menti-toggle-btn ${viewMode === 'cards' ? 'is-active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="มุมมองการ์ดความคิดเห็น"
            >
              <MessageSquareIcon size={14} />
              <span>การ์ด</span>
            </button>
            <button
              type="button"
              className={`menti-toggle-btn ${viewMode === 'cloud' ? 'is-active' : ''}`}
              onClick={() => setViewMode('cloud')}
              title="มุมมอง Word Cloud"
            >
              <CloudIcon size={14} />
              <span>Word Cloud</span>
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {groupedCards.length === 0 ? (
        <div className="menti-empty-state card text-center stack">
          <div className="menti-radar-spinner" aria-hidden="true">
            <div className="menti-radar-circle" />
            <div className="menti-radar-circle delay-1" />
            <div className="menti-radar-dot" />
          </div>
          <h3>รอคำตอบจากผู้เรียน...</h3>
          <p className="text-dim">พิมพ์ส่งคำตอบผ่านโทรศัพท์ของคุณ</p>
        </div>
      ) : viewMode === 'cards' ? (
        /* Clean Minimal Speech Cards Grid */
        <div className="menti-cards-grid">
          {groupedCards.map((item, index) => (
            <div
              key={item.text}
              className="menti-card"
              style={{ animationDelay: `${Math.min(index * 40, 350)}ms` }}
            >
              <p className="menti-card-text">{item.text}</p>
              {item.count > 1 && (
                <div className="menti-card-footer">
                  <span className="menti-count-pill" title={`${item.count} คนตอบข้อความนี้`}>
                    ×{item.count}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* Clean Word Cloud View */
        <div className="menti-word-cloud">
          {groupedCards.map((w, index) => {
            const size = Math.min(2.4, 1.15 + w.count * 0.2);
            return (
              <span
                key={w.text}
                className="menti-word-chip"
                style={{
                  fontSize: `${size}rem`,
                  animationDelay: `${index * 35}ms`,
                }}
              >
                {w.text} {w.count > 1 && <span className="menti-cloud-count">({w.count})</span>}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
